/**
 * Клас для управління підвантаженням постів.
 * 
 * @package AGDS
 * @category Modules
 * @module PostLoader
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class PostsLoader {
	/**
	 * Конструктор
	 * 
	 * @param {Object} options - Об'єкт опцій.
	 */
	constructor(options = {}) {
		this.windowWidth = options.windowWidth
		this.preloader = options.preloader
		this.api = options.api
		this.postsContainer = options.postsContainer

		this.button = this.postsContainer?.parentElement.querySelector('.loadmore-btn')

		// Керування станом 
		this.isLoading = false

		// Зберігаємо прив'язані обробники для коректного видалення
		this.scrollHandler = this.handleScroll.bind(this)
		this.clickHandler = this.loadPosts.bind(this)
	}

	/**
	 * Ініціалізація
	 * 
	 * @returns {void}
	 */
	init() {
		if (!this.postsContainer || !this.button) {
			return
		}

		// Поточна сторінка
		this.currentPage = parseInt(this.button.dataset.currentPage, 10) || 1

		// Отримуємо тип посту
		this.postType = this.button.dataset.postType

		// --- Зберігаємо UI-тексти ---
		this.sendingText = this.button.dataset.btnSending
		this.originalText = this.button.textContent

		this.button.addEventListener('click', this.clickHandler)
		window.addEventListener('scroll', this.scrollHandler, { passive: true })
	}

	/**
	 * Головний метод завантаження постів.
	 * 
	 * @async
	 * @returns {void}
	 */
	async loadPosts() {

		// Запобігаємо повторним запитам
		if (this.isLoading) return

		this.isLoading = true
		this.toggleLoadingUI(true)

		try {
			let response

			// Перевіряємо наявність фільтрів (враховуємо categories, tags та tax_query)
			const hasFilters = this.filters && (
				(this.filters.categories && this.filters.categories.length > 0) ||
				(this.filters.tags && this.filters.tags.length > 0) ||
				(this.filters.tax_query && this.filters.tax_query.length > 0)
			)

			// завжди завантажуємо next page
			const pageToLoad = this.currentPage + 1
			const archiveTermId = parseInt(this.button.dataset.termId, 10) || null

			// REST API Запит
			if (hasFilters) {
				// Якщо є фільтри - використовуємо POST (filterPosts)
				response = await this.api.postsFilter(this.postType, pageToLoad, this.filters)
			} else {
				// передаємо додаткові параметри, якщо є термін архіву
				if (archiveTermId) {
					// передаємо 'cat' як GET-параметр (server обробить його)
					response = await this.api.postsLoadMore(this.postType, pageToLoad, { cat: archiveTermId })
				} else {
					response = await this.api.postsLoadMore(this.postType, pageToLoad)
				}
			}

			if (response.data.html) {

				// Додаємо нові пости
				this.postsContainer.insertAdjacentHTML('beforeend', response.data.html)

				// Чекаємо на завантаження нових зображень для плавності
				await this.waitForImages(this.postsContainer)

				// Оновлюємо стан ТІЛЬКИ після успішного додавання
				this.currentPage = pageToLoad
				this.button.dataset.page = this.currentPage
			}

			// Чи є ще пости
			if (!response.data.has_more) {
				this.hideButton()
				this.destroyScroll()
			}

		} catch (err) {
			console.error('Error loadPosts:', err)
			this.hideButton()
			this.destroyScroll()
		} finally {
			this.isLoading = false
			this.toggleLoadingUI(false)
		}
	}

	/**
	 * Метод для скидання стану (викликається з FilterPosts)
	 * @param {Object} newFilters - Нові параметри фільтрації (напр. category ID)
	 * @param {boolean} hasMore - Чи є ще пости після першої сторінки
	 */
	reset(newFilters = {}, hasMore = true) {
		// 1. Оновлюємо внутрішні змінні
		this.filters = newFilters
		this.currentPage = 1

		// 2. Оновлюємо посилання на кнопку, оскільки фільтр міг перемалювати HTML обгортку
		this.button = this.postsContainer?.parentElement.querySelector('.loadmore-btn')
		if (!this.button) return

		// 3. Скидаємо атрибути кнопки (візуально)
		this.button.dataset.page = '1'
		this.button.dataset.currentPage = '1'
		this.button.querySelector('span').innerHTML = this.originalText
		this.button.disabled = false

		// 4. Керуємо видимістю кнопки та скролом
		if (hasMore) {
			this.button.parentElement.style.display = ''
			this.button.style.display = ''

			// Відновлюємо прослуховування скролу (якщо було видалено)
			window.removeEventListener('scroll', this.scrollHandler) // про всяк випадок видаляємо старий
			window.addEventListener('scroll', this.scrollHandler, { passive: true })
		} else {
			this.hideButton()
			this.destroyScroll()
		}
	}

	/**
	 * Обробник скролу для "нескінченного" підвантаження.
	 * Завантажує пости, коли кнопка з'являється у зоні видимості.
	 * 
	 * @returns {void}
	 */
	handleScroll() {
		if (!this.button || this.isLoading) return

		// Перевіряємо, чи кнопка у зоні видимості
		const rect = this.button.getBoundingClientRect()
		const isVisible = (
			rect.top >= 0 &&
			rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 100
		)

		if (isVisible) {
			this.loadPosts()
		}
	}

	/**
	 * Керує UI-елементами під час завантаження.
	 * 
	 * @param {boolean} isLoading - Стан завантаження.
	 * @returns {void}
	 */
	toggleLoadingUI(isLoading) {
		if (isLoading) {
			this.button.querySelector('span').innerHTML = this.sendingText
			this.button.disabled = true
			this.preloader?.classList.add('_show')
		} else {
			this.button.querySelector('span').innerHTML = this.originalText
			this.button.disabled = false
			this.preloader?.classList.remove('_show')
		}
	}

	/**
	 * Ховає кнопку та її батьківський елемент.
	 * 
	 * @returns {void}
	 */
	hideButton() {
		this.button.parentElement.style.display = 'none'
	}

	/**
	 * Чекає на повне завантаження зображень у новому HTML-блоці.
	 * 
	 * @param {HTMLElement} container - Контейнер, де з'явився новий HTML.
	 * @returns {Promise} - Проміс, який виконується, коли всі зображення завантажені.
	 */
	async waitForImages(container) {
		// Знаходимо всі зображення, які ще не завантажені
		const newImages = Array.from(
			container.querySelectorAll('img:not([data-loaded="true"])')
		);

		// Фільтруємо ті, що вже в кеші
		const imagesToLoad = newImages.filter(img => !img.complete)

		// Позначаємо всі, щоб не перевіряти їх наступного разу
		newImages.forEach(img => img.dataset.loaded = 'true')

		if (imagesToLoad.length === 0) {
			return Promise.resolve()
		}

		// Створюємо масив промісів
		const promises = imagesToLoad.map(img =>
			new Promise((resolve, reject) => {
				img.addEventListener('load', resolve, { once: true })
				img.addEventListener('error', reject, { once: true })
			})
		)

		return Promise.all(promises)
	}

	/**
	 * Видаляє обробник скролу.
	 * 
	 * @returns {void}
	 */
	destroyScroll() {
		window.removeEventListener('scroll', this.scrollHandler)
	}

	/**
	 * Повністю видаляє всі обробники подій.
	 * 
	 * @returns {void}
	 */
	destroy() {
		this.destroyScroll()
		this.button?.removeEventListener('click', this.clickHandler)

		// Скидаємо посилання
		this.postsContainer = null
		this.preloader = null
		this.button = null
	}

}